import { Component, OnDestroy } from '@angular/core';
import { Subscription, combineLatest, from, of } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { DwModalConfig, DwModalService, DwModalSize, DwModalButtonStandardConfigs } from '@devwareapps/devware-cap';
import { FitnessReferenceDataService } from '../../shared/services/fitness-reference-data.service';
import { FitnessMediaService } from '../../shared/services/fitness-media.service';
import { StreakerCentralStateService } from '../services/streaker-central-state.service';

@Component({ selector: 'app-challenge-others', templateUrl: './challenge-others.component.html', styleUrls: ['./challenge-others.component.scss'] })
export class ChallengeOthersComponent implements OnDestroy {
    personalMessage = ''; addresses: string[] = []; draft = ''; feedback = ''; error = ''; busy = false;
    completed = 0; total = 0;
    streakImageUrl = '';
    private _subscription = new Subscription();
    private _context: string;
    private _revision = 0;
    private _uncertain = new Set<string>();
    constructor(public state: StreakerCentralStateService, private _modal: DwModalService,
        private _reference: FitnessReferenceDataService, private _media: FitnessMediaService) {
        this._subscription.add(state.state$.subscribe(vm => {
            const context = `${vm.userId}:${vm.challenge?.ChallengeDefinitionId}`;
            if (this._context && context !== this._context) {
                this._revision++;
                this.addresses = []; this.draft = ''; this.personalMessage = ''; this.feedback = ''; this.error = ''; this._uncertain.clear();
            }
            this._context = context;
        }));
        this._subscription.add(state.state$.pipe(map(vm => vm.challenge),
            distinctUntilChanged((a, b) => a?.ChallengeDefinitionId === b?.ChallengeDefinitionId && a?.ProductId === b?.ProductId),
            switchMap(challenge => {
                this.streakImageUrl = '';
                if (!challenge) { return of(''); }
                // Match the home page: product artwork first, then the current streak's default banner.
                return combineLatest([this._reference.getProductPreview(challenge.ProductId),
                    this._reference.getChallengeMedia(challenge.ChallengeDefinitionId)]).pipe(take(1),
                    switchMap(([product, media]) => {
                        const id = product?.mediaId || media.DEFAULT_USER_BANNER;
                        return id ? this._media.urls([id]).pipe(take(1), map(urls => urls[id] || '')) : of('');
                    }), catchError(() => of('')));
            })).subscribe(url => this.streakImageUrl = url));
    }
    get available(): boolean {
        const vm = this.state.state, c = vm.challenge, now = Date.now();
        return !vm.loading && vm.membership === 'registered' && vm.mode !== 'after-streak' && !!c?.IsCurrent && !!this.link &&
            !!c.RegistrationStartDate && new Date(c.RegistrationStartDate).getTime() <= now &&
            !!c.RegistrationEndDate && new Date(c.RegistrationEndDate).getTime() >= now;
    }
    get link(): string {
        const code = this.state.state.profile?.MyChallengeCode;
        if (!code) { return ''; }
        const url = new URL('challenge-registration', document.baseURI);
        url.searchParams.set('challenge_code', code); return url.href;
    }
    get standardMessage(): string { return `I'm joining ${this.state.state.challenge?.Name || 'the Holiday Streak'}! Join me and keep moving this holiday season.`; }
    get message(): string { return [this.personalMessage.trim(), this.standardMessage, this.link].filter(Boolean).join('\n\n'); }
    get mailto(): string { return `mailto:?subject=${encodeURIComponent('Join me for ' + (this.state.state.challenge?.Name || 'the Holiday Streak'))}&body=${encodeURIComponent(this.message)}`; }
    get facebook(): string { return 'https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(this.link); }
    get twitter(): string { return 'https://twitter.com/intent/tweet?text=' + encodeURIComponent([this.personalMessage.trim(), this.standardMessage].filter(Boolean).join('\n\n')) + '&url=' + encodeURIComponent(this.link); }
    get sent(): number { return this.state.state.history.data.filter(row => row.EmailSent === true && row.ExcludeFromStats !== true).length; }
    async copy(text = this.message): Promise<boolean> {
        try { await navigator.clipboard.writeText(text); this.error = ''; this.feedback = 'Invitation copied.'; return true; }
        catch (_) { this.feedback = ''; this.error = 'Copy is unavailable. Select and copy the invitation below.'; return false; }
    }
    async instagram(): Promise<void> {
        const copied = await this.copy();
        const config = new DwModalConfig<{ copied: boolean; caption: string; imageUrl: string }>();
        config.title = 'Share on Instagram'; config.component = InstagramInstructionsComponent;
        config.data = { copied, caption: this.message, imageUrl: this.streakImageUrl };
        config.modalSize = DwModalSize.custom; config.customModalWidth = 'min(560px, calc(100vw - 24px))';
        config.customModalHeight = 'auto'; config.buttons = [{ ...DwModalButtonStandardConfigs.instance.closeButton }];
        this._subscription.add(this._modal.showModal(config).subscribe());
    }
    keydown(event: KeyboardEvent): void {
        if (['Enter', ',', ';'].includes(event.key)) { event.preventDefault(); this.addAddresses(); }
        if (event.key === 'Backspace' && !this.draft) { this.addresses.pop(); }
    }
    paste(event: ClipboardEvent): void {
        const text = event.clipboardData?.getData('text');
        if (text && /[\s,;]/.test(text)) { event.preventDefault(); this.draft = [this.draft, text].join(' '); this.addAddresses(); }
    }
    addAddresses(): void {
        if (this.busy) { return; }
        const parts = this.draft.split(/[\s,;]+/).filter(Boolean), invalid: string[] = [];
        parts.forEach(raw => {
            const address = raw.trim().toLowerCase();
            if (address.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) { invalid.push(raw); }
            else if (!this.addresses.includes(address)) { this.addresses.push(address); }
        });
        this.draft = invalid.join(' '); this.error = invalid.length ? 'Check the remaining email addresses.' : '';
    }
    remove(address: string): void { if (!this.busy) { this.addresses = this.addresses.filter(row => row !== address); } }
    send(): void {
        this.addAddresses();
        if (this.busy || !this.available || this.draft || !this.addresses.length) { return; }
        const known = new Set(this.state.state.history.data.map(row => (row.FriendEmail || '').toLowerCase()));
        const pending = this.addresses.filter(address => !known.has(address) && !this._uncertain.has(address));
        const skipped = this.addresses.length - pending.length;
        if (!pending.length) { this.error = 'These addresses already have invitations. Check invitation history before sending again.'; return; }
        const revision = this._revision;
        this.busy = true; this.error = ''; this.feedback = ''; this.completed = 0; this.total = pending.length;
        let saved = 0, failed = 0;
        this._subscription.add(from(pending).pipe(concatMap(address => {
            if (revision !== this._revision || !this.available) { return of({ address, ok: false, attempted: false }); }
            return this.state.saveInvitation('', address).pipe(map(() => ({ address, ok: true, attempted: true })),
                catchError(() => of({ address, ok: false, attempted: true })));
        })).subscribe(result => {
            this.completed++;
            if (revision !== this._revision) { return; }
            if (result.ok) { saved++; this.removeSaved(result.address); }
            else { failed++; if (result.attempted) { this._uncertain.add(result.address); } }
        }, () => { this.busy = false; this.error = 'Could not finish sending. Check invitation history before trying again.'; }, () => {
            this.busy = false;
            if (revision !== this._revision) { return; }
            this.feedback = `${saved} invitation${saved === 1 ? '' : 's'} submitted.${skipped ? ` ${skipped} already invited or awaiting confirmation.` : ''}`;
            if (failed) { this.error = `${failed} invitation${failed === 1 ? '' : 's'} could not be confirmed. Check invitation history before trying again.`; }
            this.state.refreshInvitationSections();
        }));
    }
    private removeSaved(address: string): void { this.addresses = this.addresses.filter(row => row !== address); }
    ngOnDestroy(): void { this._subscription.unsubscribe(); }
}

@Component({ selector: 'app-instagram-instructions', template: `
    <p role="status">{{ modalConfig.data.copied ? 'Your invitation is copied and ready to paste.' : 'Copy your invitation below to get started.' }}</p>
    <ol><li *ngIf="modalConfig.data.imageUrl">Open the streak image below and save it to your device.</li>
      <li>Open Instagram and create a post using the streak image.</li><li>Paste your invitation into the caption, then share your post.</li></ol>
    <ng-container *ngIf="modalConfig.data.imageUrl"><img [src]="modalConfig.data.imageUrl" alt="Current streak artwork" style="max-width:100%;max-height:240px;display:block;margin-bottom:12px">
      <p><a [href]="modalConfig.data.imageUrl" target="_blank" rel="noopener noreferrer">Open streak image to save</a></p></ng-container>
    <label for="instagram-caption">Your invitation</label><textarea id="instagram-caption" class="form-control" rows="5" readonly [value]="modalConfig.data.caption"></textarea>
    <p style="margin-top:16px"><a class="btn btn-primary" href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer">Open Instagram</a></p>
` })
export class InstagramInstructionsComponent {
    modalConfig: DwModalConfig<{ copied: boolean; caption: string; imageUrl: string }>;
}
